/**
 * Enhanced Admin Token System for Concurrent Requests
 * 
 * Provides robust token validation, concurrent request handling, and API service integration
 * for admin authentication. Prevents race conditions and ensures consistent authentication state.
 * 
 * Requirements addressed:
 * - 1.3: Handle concurrent token validation without conflicts
 * - 2.4: Ensure all API calls have valid authentication during simultaneous requests
 * - 2.5: Prevent authentication race conditions during navigation
 */

import { adminTokenStorage } from './adminTokenStorage';
import { tokenPersistenceManager } from './tokenPersistenceManager';
import { apiService } from './api';
import { adminAuthErrorHandler } from './adminAuthErrorHandler';

export interface AuthenticationState {
  isAuthenticated: boolean;
  token: string | null;
  expiresAt: Date | null;
  lastValidated: Date | null;
  validationInProgress: boolean;
}

export interface ConcurrentRequestConfig {
  maxConcurrentRequests?: number;
  requestTimeoutMs?: number;
  deduplicationWindowMs?: number;
}

export interface ValidationRequest {
  id: string;
  timestamp: number;
  promise: Promise<string | null>;
  resolve: (value: string | null) => void;
  reject: (error: Error) => void;
}

class AdminTokenSystem {
  private readonly config: Required<ConcurrentRequestConfig>;
  private validationQueue: Map<string, ValidationRequest> = new Map();
  private activeValidations: Set<string> = new Set();
  private authenticationState: AuthenticationState;
  private stateSubscribers: Set<(state: AuthenticationState) => void> = new Set();
  private tokenExpirationCallbacks: Set<() => void> = new Set();
  /** Prevents clear → admin:logout → clear from locking the tab. */
  private clearingAuthentication = false;
  private expirationHandling = false;

  constructor(config: ConcurrentRequestConfig = {}) {
    this.config = {
      maxConcurrentRequests: config.maxConcurrentRequests ?? 3,
      requestTimeoutMs: config.requestTimeoutMs ?? 10000,
      deduplicationWindowMs: config.deduplicationWindowMs ?? 1000
    };

    this.authenticationState = this.initializeAuthenticationState();
    
    console.log('AdminTokenSystem: Initialized with config:', this.config);
    
    // Set up token expiration monitoring
    this.setupTokenExpirationMonitoring();
  }

  /**
   * Validate token with concurrent request handling
   * Core method that prevents race conditions during simultaneous validation requests
   */
  async validateToken(token?: string): Promise<boolean> {
    const requestId = this.generateRequestId();
    
    try {
      console.log(`AdminTokenSystem: Token validation requested (ID: ${requestId})`);
      
      // Check if we can deduplicate this request
      const existingRequest = this.findDeduplicatableRequest();
      if (existingRequest) {
        console.log(`AdminTokenSystem: Deduplicating request ${requestId} with existing ${existingRequest.id}`);
        const result = await existingRequest.promise;
        return result !== null;
      }

      // Check concurrent request limits
      if (this.activeValidations.size >= this.config.maxConcurrentRequests) {
        console.log(`AdminTokenSystem: Max concurrent requests reached, queuing request ${requestId}`);
        return await this.queueValidationRequest(requestId, token);
      }

      // Perform validation
      return await this.performTokenValidation(requestId, token);

    } catch (error) {
      console.error(`AdminTokenSystem: Token validation failed (ID: ${requestId}):`, error);
      
      // Handle validation error
      const authError = error instanceof Error ? error : new Error('Token validation failed');
      await adminAuthErrorHandler.handleError(authError, { 
        showNotification: false,
        logError: true 
      });
      
      return false;
    }
  }

  /**
   * Handle concurrent validation requests safely
   * Prevents race conditions and ensures consistent results
   */
  async handleConcurrentValidation(): Promise<string> {
    const requestId = this.generateRequestId();
    
    try {
      console.log(`AdminTokenSystem: Concurrent validation requested (ID: ${requestId})`);
      
      // Use token persistence manager for the actual validation
      const token = await tokenPersistenceManager.handleConcurrentValidation();
      
      if (!token) {
        throw new Error('No valid token available');
      }

      // Update authentication state
      this.updateAuthenticationState();
      
      // Ensure API service is configured
      this.updateApiServiceAuth(token);
      
      console.log(`AdminTokenSystem: Concurrent validation successful (ID: ${requestId})`);
      return token;

    } catch (error) {
      console.error(`AdminTokenSystem: Concurrent validation failed (ID: ${requestId}):`, error);
      
      // Handle concurrent validation error
      const authError = error instanceof Error ? error : new Error('Concurrent validation failed');
      const concurrentError = adminAuthErrorHandler.createValidationError(
        authError.message,
        { requestId, context: 'concurrent_validation' }
      );
      concurrentError.type = 'CONCURRENT_REQUEST_ERROR';
      await adminAuthErrorHandler.handleError(concurrentError, { 
        showNotification: false,
        logError: true 
      });
      
      throw error;
    }
  }

  /**
   * Update API service authentication configuration
   * Ensures API service has the current valid token
   */
  updateApiServiceAuth(token: string): void {
    try {
      console.log('AdminTokenSystem: Updating API service authentication');
      
      apiService.setAdminToken(token);
      
      // Update authentication state
      this.authenticationState.token = token;
      this.authenticationState.lastValidated = new Date();
      
      // Notify subscribers
      this.notifyStateSubscribers();
      
      console.log('AdminTokenSystem: API service authentication updated successfully');
    } catch (error) {
      console.error('AdminTokenSystem: Failed to update API service auth:', error);
      
      // Handle API service update error (async but don't wait)
      const authError = error instanceof Error ? error : new Error('Failed to update API service');
      const apiError = adminAuthErrorHandler.createValidationError(
        authError.message,
        { context: 'api_service_update' }
      );
      apiError.type = 'API_SERVICE_ERROR';
      adminAuthErrorHandler.handleError(apiError, { showNotification: false }).catch(err => {
        console.error('AdminTokenSystem: Error handler failed:', err);
      });
    }
  }

  /**
   * Get current authentication state
   */
  getAuthenticationState(): AuthenticationState {
    return { ...this.authenticationState };
  }

  /**
   * Subscribe to authentication state changes
   */
  subscribeToStateChanges(callback: (state: AuthenticationState) => void): () => void {
    this.stateSubscribers.add(callback);
    
    // Return unsubscribe function
    return () => {
      this.stateSubscribers.delete(callback);
    };
  }

  /**
   * Register callback for token expiration events
   */
  onTokenExpired(callback: () => void): () => void {
    this.tokenExpirationCallbacks.add(callback);
    
    // Return cleanup function
    return () => {
      this.tokenExpirationCallbacks.delete(callback);
    };
  }

  /**
   * Clear authentication state and clean up resources
   */
  clearAuthenticationState(): void {
    // Subscribers dispatch admin:logout, and that listener calls this again.
    // Without this guard the two calls recurse on the main thread and freeze the tab.
    if (this.clearingAuthentication) {
      return;
    }

    this.clearingAuthentication = true;
    try {
      console.log('AdminTokenSystem: Clearing authentication state');

      this.validationQueue.forEach(request => {
        request.reject(new Error('Authentication cleared'));
      });
      this.validationQueue.clear();
      this.activeValidations.clear();

      this.authenticationState = {
        isAuthenticated: false,
        token: null,
        expiresAt: null,
        lastValidated: null,
        validationInProgress: false
      };

      apiService.clearAdminToken();
      this.notifyStateSubscribers();

      console.log('AdminTokenSystem: Authentication state cleared');
    } finally {
      this.clearingAuthentication = false;
    }
  }

  /**
   * Force refresh of authentication state
   */
  async refreshAuthenticationState(): Promise<boolean> {
    try {
      console.log('AdminTokenSystem: Refreshing authentication state');
      
      const token = await tokenPersistenceManager.validateAndRefreshToken();
      
      if (token) {
        this.updateApiServiceAuth(token);
        return true;
      }

      if (adminTokenStorage.isAuthenticated()) {
        const storedToken = adminTokenStorage.getToken();
        if (storedToken) {
          this.updateApiServiceAuth(storedToken);
          return true;
        }
      }

      this.clearAuthenticationState();
      return false;
    } catch (error) {
      console.error('AdminTokenSystem: Failed to refresh authentication state:', error);
      
      // Handle refresh error
      const authError = error instanceof Error ? error : new Error('Failed to refresh authentication state');
      const refreshError = adminAuthErrorHandler.createRefreshFailedError(
        authError.message,
        { context: 'authentication_state_refresh' }
      );
      await adminAuthErrorHandler.handleError(refreshError, { 
        showNotification: true,
        attemptRefresh: false 
      });
      
      this.clearAuthenticationState();
      return false;
    }
  }

  // Private methods

  /**
   * Initialize authentication state from storage
   */
  private initializeAuthenticationState(): AuthenticationState {
    const tokenInfo = adminTokenStorage.getTokenInfo();
    const isAuthenticated = adminTokenStorage.isAuthenticated();
    
    return {
      isAuthenticated,
      token: isAuthenticated ? adminTokenStorage.getToken() : null,
      expiresAt: tokenInfo ? new Date(tokenInfo.expiresAt) : null,
      lastValidated: null,
      validationInProgress: false
    };
  }

  /**
   * Update authentication state from current token storage
   */
  private updateAuthenticationState(): void {
    const tokenInfo = adminTokenStorage.getTokenInfo();
    const isAuthenticated = adminTokenStorage.isAuthenticated();
    
    this.authenticationState = {
      isAuthenticated,
      token: isAuthenticated ? adminTokenStorage.getToken() : null,
      expiresAt: tokenInfo ? new Date(tokenInfo.expiresAt) : null,
      lastValidated: new Date(),
      validationInProgress: this.activeValidations.size > 0
    };
    
    this.notifyStateSubscribers();
  }

  /**
   * Notify all state subscribers of changes
   */
  private notifyStateSubscribers(): void {
    this.stateSubscribers.forEach(callback => {
      try {
        callback(this.getAuthenticationState());
      } catch (error) {
        console.error('AdminTokenSystem: Error in state subscriber callback:', error);
      }
    });
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Find existing request that can be deduplicated
   */
  private findDeduplicatableRequest(): ValidationRequest | null {
    const now = Date.now();
    
    for (const request of this.validationQueue.values()) {
      if (now - request.timestamp < this.config.deduplicationWindowMs) {
        return request;
      }
    }
    
    return null;
  }

  /**
   * Queue validation request when concurrent limit is reached
   */
  private async queueValidationRequest(requestId: string, token?: string): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const request: ValidationRequest = {
        id: requestId,
        timestamp: Date.now(),
        promise: Promise.resolve(null), // Will be updated
        resolve: (value) => resolve(value !== null),
        reject
      };
      
      // Set up timeout
      const timeout = setTimeout(() => {
        this.validationQueue.delete(requestId);
        reject(new Error(`Validation request ${requestId} timed out`));
      }, this.config.requestTimeoutMs);
      
      // Create the actual promise
      request.promise = this.performTokenValidation(requestId, token)
        .then(result => {
          clearTimeout(timeout);
          this.validationQueue.delete(requestId);
          const tokenResult = result ? adminTokenStorage.getToken() : null;
          request.resolve(tokenResult);
          return tokenResult;
        })
        .catch(error => {
          clearTimeout(timeout);
          this.validationQueue.delete(requestId);
          request.reject(error);
          return null;
        });
      
      this.validationQueue.set(requestId, request);
      
      // Process queue when a slot becomes available
      this.processQueueWhenAvailable();
    });
  }

  /**
   * Perform actual token validation
   */
  private async performTokenValidation(requestId: string, token?: string): Promise<boolean> {
    this.activeValidations.add(requestId);
    
    try {
      console.log(`AdminTokenSystem: Performing validation for request ${requestId}`);
      
      // Update state to show validation in progress
      this.authenticationState.validationInProgress = true;
      this.notifyStateSubscribers();
      
      // Use provided token or get from storage
      const tokenToValidate = token || adminTokenStorage.getToken();
      
      if (!tokenToValidate) {
        console.log(`AdminTokenSystem: No token available for validation (${requestId})`);
        return false;
      }
      
      // Validate using token persistence manager
      const validatedToken = await tokenPersistenceManager.validateAndRefreshToken();
      
      if (validatedToken) {
        console.log(`AdminTokenSystem: Token validation successful (${requestId})`);
        this.updateAuthenticationState();
        return true;
      } else {
        console.log(`AdminTokenSystem: Token validation failed (${requestId})`);
        return false;
      }
      
    } finally {
      this.activeValidations.delete(requestId);
      this.authenticationState.validationInProgress = this.activeValidations.size > 0;
      this.notifyStateSubscribers();
    }
  }

  /**
   * Process queued requests when slots become available
   */
  private async processQueueWhenAvailable(): Promise<void> {
    // Wait for a slot to become available
    while (this.activeValidations.size >= this.config.maxConcurrentRequests) {
      await new Promise(resolve => setTimeout(resolve, 100));
    }
    
    // Process next queued request
    const nextRequest = this.validationQueue.values().next().value;
    if (nextRequest) {
      // The request will be processed by its own promise chain
      console.log(`AdminTokenSystem: Processing queued request ${nextRequest.id}`);
    }
  }

  /**
   * Set up token expiration monitoring
   */
  private setupTokenExpirationMonitoring(): void {
    // Check token expiration every minute
    setInterval(() => {
      this.checkTokenExpiration();
    }, 60000);
    
    // Voluntary logout dispatches admin:logout — do not redirect here (that breaks SPA and causes a blank screen).
    // Real expiry still flows through checkTokenExpiration → handleTokenExpiration.
    window.addEventListener('admin:logout', () => {
      if (this.clearingAuthentication || !this.authenticationState.isAuthenticated) {
        return;
      }
      console.log('AdminTokenSystem: admin:logout — clearing in-memory auth only (no redirect)');
      this.clearAuthenticationState();
    });
  }

  /**
   * Check if token is expired and handle accordingly
   */
  private checkTokenExpiration(): void {
    const tokenInfo = adminTokenStorage.getTokenInfo();
    
    if (tokenInfo) {
      const expirationTime = new Date(tokenInfo.expiresAt).getTime();
      const now = Date.now();
      
      if (now >= expirationTime) {
        console.log('AdminTokenSystem: Token expired, triggering expiration handlers');
        this.handleTokenExpiration();
      }
    }
  }

  /**
   * Handle token expiration
   */
  private handleTokenExpiration(): void {
    if (this.expirationHandling || (window as any).__sessionRedirectStarted) {
      return;
    }
    if ((window as any).__intentionalLogoutInProgress) {
      return;
    }

    this.expirationHandling = true;
    void this.renewOrRedirectExpiredAdminSession();
  }

  /**
   * Clock-expired admin JWTs can still be renewed. If renewal fails, leave the
   * dashboard and open admin login — do not clear state in a way that re-enters logout.
   */
  private async renewOrRedirectExpiredAdminSession(): Promise<void> {
    try {
      const { adminMaySilentlyExtendSession, enforceAdminIdleTimeout } = await import('../utils/sessionExpirationPolicy');
      if (!adminMaySilentlyExtendSession()) {
        await enforceAdminIdleTimeout();
        this.expirationHandling = false;
        return;
      }

      const refreshed = await apiService.refreshAdminToken();
      if (refreshed.success && refreshed.token) {
        console.log('AdminTokenSystem: Expired admin token renewed');
        this.updateAuthenticationState();
        this.expirationHandling = false;
        return;
      }

      const { redirectAdminForSecurity, adminIdleLimitExceeded } = await import('../utils/sessionExpirationPolicy');
      await redirectAdminForSecurity(adminIdleLimitExceeded() ? 'session_timeout' : 'session_expired');
      this.expirationHandling = false;
      return;
    } catch (error) {
      console.warn('AdminTokenSystem: Admin token refresh on expiry failed:', error);
      if (!(window as any).__sessionRedirectStarted) {
        const { isSecurityRejection, redirectAdminForSecurity, adminIdleLimitExceeded } = await import('../utils/sessionExpirationPolicy');
        if (adminIdleLimitExceeded() || isSecurityRejection(error)) {
          await redirectAdminForSecurity(adminIdleLimitExceeded() ? 'session_timeout' : 'session_expired');
        }
      }
    } finally {
      if (!(window as any).__sessionRedirectStarted) {
        this.expirationHandling = false;
      }
    }
  }
}

// Export singleton instance
export const adminTokenSystem = new AdminTokenSystem();

// Types are already exported above with their definitions